import { ReactNode } from "react";
import { NavLink } from "@/components/ui/navigation";
import { EyeIcon, HeartIcon, RocketLaunchIcon } from "@heroicons/react/20/solid";
import { MemoryIcon } from "@/assets/icons";
import { Link } from "wouter";
import { Logo } from "../ui/logo";
import { StudyFlowSteps } from "../ui/flow-steps";
import { useGetStudy } from "@/api/studio/studies/queries";
import { StudyDropdown } from "@/features/studies/components/study-dropdown";
import { EntitlementGate } from "@/features/auth/components/entitlement-gate";
import { PadlockIcon } from "@/assets/icons";
import { useSegment } from "@/hooks/use-segment";
import { useReportsContext } from "@/features/reports/contexts/reports-context";
import { Badge } from "../ui/badge";

/* Prop Types */
interface LayoutProps {
  projectId: string;
  studyId: string;
  children: ReactNode;
  hideSidebar?: boolean;
}

/*
 * ReportsLayout component
 * This layout wraps the reports pages in the application
 */
export const ReportsLayout = ({ projectId, studyId, children, hideSidebar }: LayoutProps) => {
  /* Queries */
  const { data: study, isLoading: isStudyLoading } = useGetStudy(projectId, studyId);

  /* Context */
  const {
    state: { view },
  } = useReportsContext();

  /* Hooks */
  const { segmentTrackEvent } = useSegment();

  /* Handlers */
  const handleReportClick = (reportType: string) => {
    // Segment tracking
    segmentTrackEvent("Study Report Viewed", {
      projectId,
      studyId,
      reportType,
      viewType: view.toLowerCase(),
    });
  };

  /* Render */
  return (
    <div className="grow flex flex-col gap-4 px-7 py-4 max-h-screen">
      {/* Header */}
      <header className="min-h-12 flex items-center">
        <Link href="/" className="flex items-center gap-2">
          <Logo /> <h3 className="font-semibold">Studio</h3>
        </Link>

        {/* Asset steps */}
        <StudyFlowSteps projectId={projectId} studyId={studyId} currentStep="reports" />

        {/* Study dropdown */}
        {study && !isStudyLoading && (
          <div className="ml-auto">
            <StudyDropdown projectId={projectId} study={study} />
          </div>
        )}
      </header>

      <div className="grow flex flex-row gap-4 max-h-full overflow-hidden">
        {/* Side bar */}
        {!hideSidebar && (
          <div className="group flex flex-col w-10 hover:w-52 min-w-10 hover:min-w-44 transition-all duration-300 ease-in-out">
            <NavLink href={`/projects/${projectId}/studies/${studyId}/${study?.type}/reports/overview`} className="pr-0" onClick={() => handleReportClick("creative impact")}>
              <RocketLaunchIcon className="size-4.5 flex-shrink-0" />
              <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap overflow-hidden">Creative Impact</span>
            </NavLink>
            <div className="h-[1px] bg-base-200 my-1"></div>
            <NavLink href={`/projects/${projectId}/studies/${studyId}/${study?.type}/reports/attention/overview`} onClick={() => handleReportClick("attention")}>
              <EyeIcon className="size-4.5 flex-shrink-0" />
              <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap overflow-hidden">Attention</span>
            </NavLink>
            <EntitlementGate
              entitlement="memoryEmotion"
              fallback={
                <NavLink href={`/projects/${projectId}/studies/${studyId}/${study?.type}/reports/emotion/overview`} onClick={() => handleReportClick("emotion")}>
                  <HeartIcon className="size-4.5 flex-shrink-0" />
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap overflow-hidden">Emotion</span>
                  <Badge className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-base-100 p-1.5 rounded-xl flex items-center gap-1.5">
                    <PadlockIcon isGradient={true} />
                    <span className="text-xs font-medium bg-gradient-to-r from-[#4E499C] to-[#F2318A] bg-clip-text text-transparent whitespace-nowrap">Book demo</span>
                  </Badge>
                </NavLink>
              }
            >
              <NavLink href={`/projects/${projectId}/studies/${studyId}/${study?.type}/reports/emotion/overview`} onClick={() => handleReportClick("emotion")}>
                <HeartIcon className="size-4.5 flex-shrink-0" />
                <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap overflow-hidden">Emotion</span>
                <Badge className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-1.5 rounded-xl flex items-center gap-1.5 bg-base-200 text-xs">New</Badge>
              </NavLink>
            </EntitlementGate>
            <EntitlementGate
              entitlement="memoryEmotion"
              fallback={
                <NavLink href={`/projects/${projectId}/studies/${studyId}/${study?.type}/reports/memory/overview`} onClick={() => handleReportClick("memory")}>
                  <MemoryIcon className="size-4.5 flex-shrink-0" />
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap overflow-hidden">Memory</span>
                  <Badge className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-base-100 p-1.5 rounded-xl flex items-center gap-1.5">
                    <PadlockIcon isGradient={true} />
                    <span className="text-xs font-medium bg-gradient-to-r from-[#4E499C] to-[#F2318A] bg-clip-text text-transparent whitespace-nowrap">Book demo</span>
                  </Badge>
                </NavLink>
              }
            >
              <NavLink href={`/projects/${projectId}/studies/${studyId}/${study?.type}/reports/memory/overview`} onClick={() => handleReportClick("memory")}>
                <MemoryIcon className="size-4.5 flex-shrink-0" />
                <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap overflow-hidden">Memory</span>
                <Badge className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-1.5 rounded-xl flex items-center gap-1.5 bg-base-200 text-xs">New</Badge>
              </NavLink>
            </EntitlementGate>
          </div>
        )}

        {/* Children Render */}
        <div className="grow flex flex-col gap-4">{children}</div>
      </div>
    </div>
  );
};
